<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">持券记录</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 张</span>
        <span class="divider">|</span>
        <span class="text-warning">未使用 <b>{{ statusCount(0) }}</b></span>
        <span class="divider">|</span>
        <span>已使用 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>已过期 <b>{{ statusCount(2) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input
              v-model="query.keyword"
              placeholder="昵称 / 核销码"
              clearable
              style="width: 190px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="券名称">
            <el-select v-model="query.benefit_id" placeholder="全部" clearable style="width: 200px">
              <el-option v-for="b in benefitOptions" :key="b.id" :label="b.name" :value="b.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 110px">
              <el-option v-for="s in USER_BENEFIT_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="发放时间">
            <el-date-picker
              v-model="query.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-s-promotion" size="small" @click="handleGrant">指定发放</el-button>
          <el-button icon="el-icon-download" size="small" @click="handleExport">导出记录</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 持券人 -->
        <el-table-column label="持券人" min-width="150">
          <template slot-scope="scope">
            <div class="user-cell">
              <el-image
                v-if="scope.row.avatar"
                class="user-avatar user-avatar--img"
                :src="scope.row.avatar"
                fit="cover"
              >
                <span slot="error" class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                  {{ (scope.row.nickname || '客')[0] }}
                </span>
              </el-image>
              <span v-else class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                {{ (scope.row.nickname || '客')[0] }}
              </span>
              <div class="user-main">
                <span class="user-name">{{ scope.row.nickname || '未设置昵称' }}</span>
                <span class="user-id">编号 {{ scope.row.user_id }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 券名称 -->
        <el-table-column label="券名称" min-width="170" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.benefit_name || '—' }}</template>
        </el-table-column>
        <!-- 发放方式 -->
        <el-table-column label="发放方式" width="110" align="center">
          <template slot-scope="scope">
            <el-tooltip
              :disabled="!(isManualSource(scope.row.source_type) && scope.row.source_desc)"
              :content="'发放备注：' + scope.row.source_desc"
              placement="top"
            >
              <el-tag :type="benefitSourceTagType(scope.row.source_type)" size="mini" effect="plain">
                {{ benefitSourceText(scope.row.source_type) }}
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>
        <!-- 核销码 -->
        <el-table-column label="核销码" width="150">
          <template slot-scope="scope">
            <span class="code-text">{{ scope.row.verify_code }}</span>
          </template>
        </el-table-column>
        <!-- 有效期 -->
        <el-table-column label="有效期" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <!-- expire_time 为空 = 长期有效券种（有效期类型 3），不能显示成「起始 至 」 -->
            <span v-if="scope.row.start_time">
              {{ (scope.row.start_time || '').slice(0, 10) }}
              {{ scope.row.expire_time ? ' 至 ' + scope.row.expire_time.slice(0, 10) : ' 起长期有效' }}
            </span>
            <span v-else-if="!scope.row.expire_time" class="cell-muted">长期有效</span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="userBenefitStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ userBenefitStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 发放时间 -->
        <el-table-column label="发放时间" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.created_at }}</template>
        </el-table-column>
        <!-- 核销时间 -->
        <el-table-column label="核销时间" width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.use_time">{{ scope.row.use_time }}</span>
            <span v-else class="cell-muted">未核销</span>
          </template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 券详情弹框 -->
    <el-dialog
      title="持券详情"
      :visible.sync="detailVisible"
      width="620px"
      top="7vh"
      class="ticket-dialog"
      append-to-body
    >
      <div v-loading="detailLoading" class="detail-wrap">
        <template v-if="detailRow">
          <!-- 券票 -->
          <div class="ticket">
            <div class="ticket-left">
              <el-image
                v-if="detailRow.benefit_cover"
                :src="detailRow.benefit_cover"
                :preview-src-list="[detailRow.benefit_cover]"
                fit="cover"
                class="ticket-cover"
              />
              <div v-else class="ticket-cover ticket-cover--empty">暂无封面</div>
              <div class="ticket-name">{{ detailRow.benefit_name || '—' }}</div>
              <div class="ticket-cat">{{ benefitCategoryText(detailRow.benefit_category) }}类权益券</div>
            </div>
            <div class="ticket-right">
              <template v-if="canViewFullCode">
                <el-image class="qr-img" :src="detailRow.qrcode" :preview-src-list="[detailRow.qrcode]" fit="contain" />
                <div class="code-big">{{ formatVerifyCode(detailRow.verify_code_full) }}</div>
                <div class="code-tip">出示此码由工作人员扫码核销</div>
              </template>
              <template v-else>
                <div class="qr-locked">
                  <i class="el-icon-lock" />
                  <span>二维码已隐藏</span>
                </div>
                <div class="code-big code-masked">{{ detailRow.verify_code }}</div>
                <div class="code-tip">完整核销码需申请查看权限</div>
              </template>
            </div>
          </div>

          <!-- 记录信息 -->
          <div class="detail-fields">
            <div class="field-row">
              <span class="field-label">持券人</span>
              <span>{{ detailRow.nickname || '未设置昵称' }}（编号 {{ detailRow.user_id }}）</span>
            </div>
            <div class="field-row">
              <span class="field-label">券状态</span>
              <el-tag :type="userBenefitStatusTagType(detailRow.status)" size="mini" effect="plain">
                {{ userBenefitStatusText(detailRow.status) }}
              </el-tag>
            </div>
            <div class="field-row">
              <span class="field-label">发放方式</span>
              <span class="field-source">
                <el-tag :type="benefitSourceTagType(detailRow.source_type)" size="mini" effect="plain">
                  {{ benefitSourceText(detailRow.source_type) }}
                </el-tag>
                <span v-if="detailRow.source_desc" class="field-sub">{{ detailRow.source_desc }}</span>
              </span>
            </div>
            <div class="field-row">
              <span class="field-label">发放人</span>
              <span>{{ detailRow.operator_name || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">发放时间</span>
              <span>{{ detailRow.created_at }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">生效时间</span>
              <span>{{ detailRow.start_time || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">过期时间</span>
              <!-- 空 = 长期有效券种（有效期类型 3） -->
              <span>{{ detailRow.expire_time || '长期有效（不设到期时间）' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">核销时间</span>
              <span>{{ detailRow.use_time || '未核销' }}</span>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 指定发放弹框 -->
    <benefit-grant-dialog
      :visible.sync="grantVisible"
      @granted="handleGranted"
    />
  </div>
</template>

<script>
import {
  USER_BENEFIT_STATUS,
  userBenefitStatusText, userBenefitStatusTagType, formatVerifyCode,
  buildQrImage
} from '@/mock/userBenefit'
import {
  benefitCategoryText, benefitSourceText, benefitSourceTagType
} from '@/mock/benefit'
import { getUserBenefitList, getUserBenefitDetail, getBenefitOptions } from '@/api/benefit'
import BenefitGrantDialog from '@/components/BenefitGrantDialog.vue'

export default {
  name: 'UserBenefitManage',
  components: { BenefitGrantDialog },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      // 头部统计（后端全量计数，不受当页筛选影响）
      statusCounts: {},
      query: { keyword: '', benefit_id: '', status: '', dateRange: null },
      // 券名称下拉（含已停用券种，历史记录也能筛）
      benefitOptions: [],
      detailVisible: false,
      detailLoading: false,
      detailRow: null,
      grantVisible: false,
      USER_BENEFIT_STATUS,
      userBenefitStatusText,
      userBenefitStatusTagType,
      formatVerifyCode,
      benefitCategoryText,
      benefitSourceText,
      benefitSourceTagType
    }
  },
  computed: {
    // 完整核销码与二维码属敏感信息，由后端按权限放行（无权限时 verify_code_full 为空）
    canViewFullCode() {
      return !!(this.detailRow && this.detailRow.verify_code_full)
    }
  },
  created() {
    this.loadBenefitOptions()
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.benefit_id !== '' && this.query.benefit_id !== null) params.benefit_id = this.query.benefit_id
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status
      if (this.query.dateRange && this.query.dateRange.length === 2) {
        params.date_range = this.query.dateRange
      }
      getUserBenefitList(params).then(res => {
        if (res.code !== 200) {
          this.loading = false
          return
        }
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.statusCounts = data.status_counts || {}
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    loadBenefitOptions() {
      getBenefitOptions({ all: 1 }).then(res => {
        if (res.code !== 200) return
        this.benefitOptions = res.data || []
      }).catch(() => {})
    },
    statusCount(val) {
      return this.statusCounts[String(val)] || 0
    },
    // 发放来源是固定枚举（0 后台指定 / 1 每月发放 / 2 里程碑发放），中文与配色走本地映射
    isManualSource(id) {
      return Number(id || 0) === 0
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', benefit_id: '', status: '', dateRange: null }
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleExport() {
      this.$message.info('导出功能待开放')
    },
    handleDetail(row) {
      this.detailRow = null
      this.detailVisible = true
      this.detailLoading = true
      getUserBenefitDetail(row.id).then(res => {
        this.detailLoading = false
        if (res.code !== 200) return
        const detail = res.data || {}
        // 二维码按完整核销码本地生成，后端不落图
        detail.qrcode = detail.verify_code_full ? buildQrImage(detail.verify_code_full) : ''
        this.detailRow = detail
      }).catch(() => {
        this.detailLoading = false
      })
    },
    handleGrant() {
      this.grantVisible = true
    },
    handleGranted() {
      this.page = 1
      this.loadData()
    }
  }
}
</script>

<style scoped>
/* 持券人 */
.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.user-avatar {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.user-avatar--female {
  background: #a4686b;
}
/* 真实头像：el-image 内图吃满圆框 */
.user-avatar--img >>> img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}
.user-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}
.user-name {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-id {
  font-size: 11px;
  color: #a8abb2;
}
.code-text {
  font-family: Consolas, Monaco, monospace;
  font-size: 12.5px;
  color: #4d4d4d;
  letter-spacing: 0.6px;
}
.cell-muted {
  color: #c0c4cc;
  font-size: 12px;
}
.text-warning {
  color: #b88230;
}

/* 券票 */
.ticket-dialog >>> .el-dialog__body {
  padding: 14px 30px 6px;
}
.detail-wrap {
  min-height: 120px;
}
.ticket {
  display: flex;
  gap: 18px;
  padding: 16px 18px;
  border-radius: 10px;
  background: #faf7f1;
  border: 1px solid #f0ebe2;
}
.ticket-left {
  flex: 1;
  min-width: 0;
}
.ticket-cover {
  width: 100%;
  height: 118px;
  border-radius: 8px;
  overflow: hidden;
  background: #ece3d6;
}
.ticket-cover--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8ac9c;
  font-size: 12px;
}
.ticket-name {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.ticket-cat {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.ticket-right {
  flex: 0 0 168px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 18px;
  border-left: 1px dashed #ddd2bf;
}
.qr-img {
  width: 112px;
  height: 112px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #efe8dd;
}
.code-big {
  margin-top: 10px;
  font-family: Consolas, Monaco, monospace;
  font-size: 14px;
  letter-spacing: 1.2px;
  color: #303133;
}
.code-masked {
  letter-spacing: 2px;
  color: #a8a29a;
}
.qr-locked {
  width: 112px;
  height: 112px;
  border-radius: 6px;
  background: #f6f3ee;
  border: 1px dashed #ded5c8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #b8ac9c;
  font-size: 12px;
}
.qr-locked i {
  font-size: 22px;
}
.code-tip {
  margin-top: 4px;
  font-size: 11px;
  color: #a8abb2;
  text-align: center;
}

/* 字段清单 */
.detail-fields {
  margin-top: 14px;
  border-top: 1px solid #f0f0f0;
}
.field-row {
  display: flex;
  align-items: center;
  padding: 9px 0;
  border-bottom: 1px dashed #f0f0f0;
  font-size: 13px;
  color: #303133;
}
.field-row:last-child {
  border-bottom: none;
}
.field-label {
  flex: 0 0 90px;
  color: #909399;
}
.field-source {
  display: flex;
  align-items: center;
  gap: 8px;
}
.field-sub {
  font-size: 12px;
  color: #909399;
}
</style>
